import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { graphql, useStaticQuery } from "gatsby";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { getImage, GatsbyImage } from "gatsby-plugin-image";
import "../../scss/main.scss";
import {
	Newsblock,
	NewsblockSection,
	NewsblockContent,
	NewsblockBlocks,
	NewsblockTitle,
} from '../components/Newsblock';

const CATEGORY_CONFIG = [
	{
		slug: "starting-the-conversation",
		name: "Starting the conversation",
		order: [
			"When is care needed?",
			"Discussing care options as a family",
			"What to Do When a Parent Refuses Care",
			"When Your Parent Doesn't Want a Stranger in the House",
			"Live-in care vs care home",
			"Is Live-in Care Only for Dementia? Clearing Up a Common Assumption",
			"What can a live-in carer help with?",
			"Live-in Care Company vs Matching Agency: What's the Difference?",
			"Live-in Care Explained: How It Compares to a Nanny or Au Pair",
			"Balancing Your Own Life While Caring for an Ageing Parent",
			"When Both Parents Need Care at the Same Time",
			"Grandparents Raising Grandchildren While Managing Their Own Care Needs",
			"How to find the right carer for you",
			"What is a care consultation?",
			"What is a care assessment?",
			"How quickly can live-in care start?",
			"Is night-time care included in live-in care?",
			"What is the minimum cover for care?",
		],
	},
	{
		slug: "paying-for-care",
		name: "Paying for care",
		order: [
			"Cost of live-in care vs care home",
			"What Is the Cost of Live-in Care",
			"What are my funding options?",
			"Self-funding care",
			"State funding (CHC)",
			"Local authority funding",
			"How Do Payments Work at Edyn",
		],
	},
	{
		slug: "getting-care-in-place",
		name: "Getting Care in place",
		order: [
			"How to choose the right live-in care provider",
			"How Edyn Works: From First Call to Carer Match",
			"CQC vs Introductory Agency",
			"Is Edyn CQC registered, and what does that mean?",
			"What makes Edyn different?",
			"Which one of Edyn's packages fits your situation?",
			"What If I'm Not Happy With My Carer",
			"Live-in Care for Younger Adults With Disabilities or Chronic Illness",
			"Live-in Care for a Loved One With Sight or Hearing Loss",
			"Live-in Care for Multi-Generational and Multicultural Households",
			"How does Edyn recruit its carers?",
			"How does Edyn ensure quality?",
			"The Professional Standards Our Carers Uphold",
			"Does Live-in Care Cover Rural Areas?",
			"Live-in Care in Rural Areas vs Cities: What's Different?",
			"Where does Edyn operate?",
			"Not Comfortable With Technology? Here's How We Make Care Work for You",
			"Edyn's Software",
			"Best Home Care Employer (London) at the Great British Care Awards 2025",
		],
	},
	{
		slug: "life-with-a-live-in-carer",
		name: "Life With a Live-in Carer",
		order: [
			"What Happens in the First Week of Live-in Care",
			"A Day in the Life With a Live-in Carer",
			"What Happens If Your Live-in Carer Is Unwell or Needs Cover?",
			"What Does a Live-in Carer Need in Your Home?",
			"Keeping a Parent's Routines and Rituals Alive",
			"Pets and Live-in Care: Keeping the Whole Household Comfortable",
			"Do I Need to Provide Food for My Carer?",
			"Can a Live-in Carer Also Help My Spouse?",
			"Can I Have a Carer Who Speaks My Loved One's Language?",
			"Can a Live-in Carer Drive My Loved One?",
			"Helping a Parent Stay Engaged in Their Community With Live-in Support",
			"How Do Edyn's Carers Handle Food Safety?",
			"Why Live-in Carers Need Breaks",
			"Why Our Carers Can't Accept Gifts (and How to Say Thank You)",
			"Home Surveillance and Care: The Benefits, and What to Be Conscious Of",
			"How to make a home safe for the elderly",
			"Keeping Elderly Relatives Socially Connected Over the Holidays (seasonal)",
		],
	},
	{
		slug: "safety-health-and-wellbeing",
		name: "Safety, Health & Wellbeing",
		order: [
			"What Happens If There Is an Accident",
			"How Do Our Carers Handle Medication Safely",
			"UTIs and Sudden Confusion in Older Adults: Why It Is Often Mistaken for Dementia",
			"Mental Capacity",
			"Moving and Handling",
			"Dehydration in Older Adults: A Commonly Missed Risk at Home",
			"How Do We Keep Your Loved One's Money Safe",
			"Companionship as Care: Why Loneliness Is a Health Issue Too",
			"Sleep Problems in Older Adults: Causes and Home-Based Solutions",
			"End-of-life care",
			"Long-Distance Caregiving: Staying Involved When You Live Far Away",
			"Heatwave Safety for Older Adults",
			"Winter Safety for Older Adults",
		],
	},
	{
		slug: "hospital-and-care-transitions",
		name: "Hospital & Care Transitions",
		order: [
			"What Happens When Your Loved One Goes Into Hospital?",
			"What Happens During a Hospital/Care Home Discharge",
			"Rehabilitation Care at Home: Support for Recovery After Hospital",
		],
	},
	{
		slug: "live-in-care-by-region",
		name: "Live-in Care by Region",
		order: [
			"Live-in Care in London",
			"Live-in Care in the South East",
			"Live-in Care in the South West",
			"Live-in Care in the East Midlands",
			"Live-in Care in the West Midlands",
			"Live-in Care in the North East",
			"Live-in Care in the North West",
			"Live-in Care in Yorkshire and The Humber",
			"Live-in Care in the East of England",
		],
	},
];

const ORDER_TITLE_ALIASES = {
	"when is care needed": [
		"When is care needed? How to spot the signs, and what to do next",
	],
	"live in care vs care home": [
		"Live-in care vs. care home: Which option is right?",
		"Live-in care vs. Care Home",
	],
	"what is the minimum cover for care": [
		"What Is the Minimum Cover Time for Live-in Care?",
	],
	"cost of live in care vs care home": [
		"Cost of a Care Home vs Live-in Care: The Surprising Truth",
	],
	"what is the cost of live in care": [
		"What Is the Cost of Live-in Care?",
		"What is the cost of live-in care?",
	],
	"what are my funding options": [
		"What Are My Funding Options for Care?",
	],
	"self funding care": [
		"Self-Funding Care: Your Options Explained",
		"Self-funding care",
	],
	"state funding chc": [
		"A Guide to State-Funded Care",
		"State and local authority funding",
	],
	"local authority funding": [
		"Local Authority Funding for Care",
	],
	"how do payments work at edyn": [
		"How Do Payments Work at Edyn?",
	],
	"what makes edyn different": [
		"Timeless Care, Modern Thinking: What Makes Edyn Different",
	],
	"which one of edyns packages fits your situation": [
		"Which of Edyn's Packages Fits Your Situation?",
	],
	"what if im not happy with my carer": [
		"What If I'm Not Happy With My Carer?",
	],
	"not comfortable with technology heres how we make care work for you": [
		"Not Comfortable With Technology? Here's How We Make Care Work for You",
	],
	"edyns software": [
		"Technology That Makes Care More Human",
	],
	"how do our carers handle medication safely": [
		"How Do Our Carers Handle Medication Safely?",
	],
	"how do we keep your loved ones money safe": [
		"How Do We Keep Your Loved One's Money Safe?",
	],
	"mental capacity": [
		"Mental Capacity: How Our Carers Respect Your Loved One's Right to Choose",
	],
	"moving and handling": [
		"Moving and Handling: How Our Carers Keep Your Loved One Safe and Mobile",
	],
	"end of life care": [
		"End-of-Life Care at Home",
	],
	"what happens during a hospital care home discharge": [
		"What Happens When Your Loved One Is Discharged From Hospital or a Care Home?",
	],
	"live in care in yorkshire and the humber": [
		"Live-in Care in Yorkshire and the Humber",
	],
};

const DESKTOP_CAROUSEL_QUERY = "(min-width: 1024px)";

const truncateExcerpt = (excerpt) => {
	if (!excerpt || excerpt.length <= 220) {
		return excerpt;
	}

	const cutoffIndex = excerpt.indexOf(" ", 220);
	return cutoffIndex === -1 ? `${excerpt} ...` : `${excerpt.slice(0, cutoffIndex)} ...`;
};

const normalizeTitle = (value = "") =>
	value
		.toLowerCase()
		.replace(/&/g, " and ")
		.replace(/[^a-z0-9\s]/g, " ")
		.replace(/\s+/g, " ")
		.trim();

const titleMatchesRule = (title, rule) => {
	const normalizedTitle = normalizeTitle(title);
	const normalizedRule = normalizeTitle(rule);

	if (!normalizedTitle || !normalizedRule) {
		return false;
	}

	return (
		normalizedTitle === normalizedRule ||
		normalizedTitle.includes(normalizedRule) ||
		normalizedRule.includes(normalizedTitle)
	);
};

const getOrderRank = (title, orderRules) => {
	for (let index = 0; index < orderRules.length; index += 1) {
		const rule = orderRules[index];
		const possibleTitles = [rule, ...(ORDER_TITLE_ALIASES[normalizeTitle(rule)] || [])];

		if (possibleTitles.some((possibleTitle) => titleMatchesRule(title, possibleTitle))) {
			return index;
		}
	}

	return Number.MAX_SAFE_INTEGER;
};

const sortPostsByConfiguredOrder = (posts, orderRules) =>
	posts
		.map((post, index) => ({
			post,
			index,
			rank: getOrderRank(post.title, orderRules),
		}))
		.sort((left, right) => {
			if (left.rank !== right.rank) {
				return left.rank - right.rank;
			}

			return left.index - right.index;
		})
		.map(({ post }) => post);

const getVisiblePageIndexes = (pageCount, pageIndex, maxVisibleDots) => {
	if (pageCount <= maxVisibleDots) {
		return Array.from({ length: pageCount }, (_, index) => index);
	}

	const halfWindow = Math.floor(maxVisibleDots / 2);
	let startIndex = Math.max(0, pageIndex - halfWindow);
	let endIndex = startIndex + maxVisibleDots;

	if (endIndex > pageCount) {
		endIndex = pageCount;
		startIndex = Math.max(0, endIndex - maxVisibleDots);
	}

	return Array.from({ length: endIndex - startIndex }, (_, index) => startIndex + index);
};

function CategoryCarousel({ posts }) {
	const [visiblePosts, setVisiblePosts] = useState(3);
	const [pageIndex, setPageIndex] = useState(0);
	const isSingleColumnCarousel = visiblePosts === 1;
	const pageCount = Math.max(1, Math.ceil(posts.length / visiblePosts));
	const currentPosts = posts.slice(pageIndex * visiblePosts, (pageIndex + 1) * visiblePosts);
	const visiblePageIndexes = getVisiblePageIndexes(pageCount, pageIndex, isSingleColumnCarousel ? 5 : pageCount);

	useEffect(() => {
		if (typeof window === "undefined") {
			return undefined;
		}

		const mediaQuery = window.matchMedia(DESKTOP_CAROUSEL_QUERY);
		const updateVisiblePosts = () => {
			setVisiblePosts(mediaQuery.matches ? 3 : 1);
		};

		updateVisiblePosts();

		if (mediaQuery.addEventListener) {
			mediaQuery.addEventListener("change", updateVisiblePosts);
			return () => mediaQuery.removeEventListener("change", updateVisiblePosts);
		}

		mediaQuery.addListener(updateVisiblePosts);
		return () => mediaQuery.removeListener(updateVisiblePosts);
	}, []);

	useEffect(() => {
		setPageIndex(0);
	}, [visiblePosts, posts.length]);

	const updatePage = (nextPageIndex) => {
		if (nextPageIndex < 0) {
			setPageIndex(pageCount - 1);
			return;
		}

		if (nextPageIndex >= pageCount) {
			setPageIndex(0);
			return;
		}

		setPageIndex(nextPageIndex);
	};

	return (
		<div className="blog-category-carousel">
			<NewsblockBlocks>
				{currentPosts.map(({ title, slug, excerpt, published_at, feature_image_sharp }) => (
					<Newsblock
						key={slug}
						className="blog-category-carousel__card"
						color="yellow"
						date={published_at}
						title={title}
						description={truncateExcerpt(excerpt)}
						link={`/blog/${slug}/`}>
						{feature_image_sharp && (
							<GatsbyImage
								placeholder="blurred"
								image={getImage(feature_image_sharp)}
								alt={title}
							/>
						)}
					</Newsblock>
				))}
			</NewsblockBlocks>
			{pageCount > 1 && (
				<div className="blog-category-carousel__controls">
					<button
						type="button"
						className="blog-category-carousel__button"
						onClick={() => updatePage(pageIndex - 1)}>
						Previous
					</button>
					<div className="blog-category-carousel__dots" aria-label="Carousel pagination">
						{visiblePageIndexes.map((index) => (
							<button
								type="button"
								key={`page-${index + 1}`}
								className={`blog-category-carousel__dot${index === pageIndex ? " is-active" : ""}`}
								onClick={() => updatePage(index)}
								aria-label={`Show page ${index + 1}`}
								aria-current={index === pageIndex ? "page" : undefined}
							/>
						))}
					</div>
					<button
						type="button"
						className="blog-category-carousel__button"
						onClick={() => updatePage(pageIndex + 1)}>
						Next
					</button>
				</div>
			)}
		</div>
	);
}

export default function Blog() {
	const { allGhostPost: { nodes: blogPosts }  } = useStaticQuery(
		graphql`
			query {
				allGhostPost(sort: { order: DESC, fields: [published_at] }) {
					nodes {
						title
						slug
						excerpt
						published_at(formatString: "DD MMM YYYY")
						feature_image_sharp {
							childImageSharp {
								gatsbyImageData
							}
						}
						primary_tag {
							name
							slug
						}
					}
				}
		}
	`)

	const categorySections = blogPosts.reduce((sections, post) => {
		const tag = post.primary_tag

		if (!tag || tag.slug === "test") {
			return sections
		}

		const existingSection = sections.find(section => section.slug === tag.slug)

		if (existingSection) {
			existingSection.posts.push(post)
			return sections
		}

		sections.push({
			name: tag.name,
			slug: tag.slug,
			posts: [post],
		})

		return sections
	}, [])

	const orderedCategorySections = [
		...CATEGORY_CONFIG.map(({ slug, name, order }) => {
			const matchingSection = categorySections.find((section) => section.slug === slug);

			if (!matchingSection) {
				return null;
			}

			return {
				...matchingSection,
				name,
				posts: sortPostsByConfiguredOrder(matchingSection.posts, order),
			};
		}).filter(Boolean),
		...categorySections.filter(({ slug }) => !CATEGORY_CONFIG.some((section) => section.slug === slug)),
	]

	return (
		<>
			<Helmet>
				<meta charset="UTF-8" />
				<meta http-equiv="X-UA-Compatible" content="IE=edge" />
				
				<meta name="viewport" content="width=device-width, initial-scale=1.0" />
				<title>Edyn Care | Blog</title>
				<meta property="og:title" content="Edyn Care | Blog"/>
				<meta property="og:description" content="High quality care in the comfort of your own home."/>
				<meta property="og:image" content="https://www.edyn.care/assets/marketing-site/images/open-graph-img.png"/>
				<meta property="og:url" content="https://www.edyn.care/blog" />
				<meta name="twitter:card" content="summary_large_image" />
				<meta property="twitter:domain" content="edyn.care" />
				<meta property="twitter:url" content="https://www.edyn.care/blog" />
				<meta name="twitter:title" content="Edyn Care | Blog" />
				<meta name="twitter:description" content="High quality care in the comfort of your own home." />
				<meta name="twitter:image" content="https://www.edyn.care/assets/marketing-site/images/open-graph-img.png" />
			</Helmet>
			<div className="container">
				<Header />
				<main className="blog-landing-page">
					<div className="blog-landing">
						<div className="blog-landing-content">
							<div className="drop-down">
								<h1>Stories and news from the Edyn team and clients</h1>
							</div>
						</div>
						{/*<StaticImage placeholder="blurred"
							className="blog-landing-right"
							src="../../static/assets/marketing-site/images/blog-landing-texture.png"
						/>*/}
					</div>
					{orderedCategorySections.map(({ name, slug, posts }) => (
						<NewsblockSection className="newsblock-latest" key={slug}>
							<NewsblockContent>
								<NewsblockTitle center displaySmall>
									{name}
								</NewsblockTitle>
								<CategoryCarousel posts={posts} />
							</NewsblockContent>
						</NewsblockSection>
					))}
					<Footer />
				</main>
			</div>
		</>
	);
}
